import { Injectable, Pipe, PipeTransform } from '@angular/core';
import moment from 'moment';
import { toJalaali, jalaaliMonthLength, toGregorian } from 'jalaali-js';

//import * as jalaali from 'jalaali-js';
import { YearItem } from '../models/new-calender-models/year-item';
import { MonthItem } from '../models/new-calender-models/month-item';
import { DayItem } from '../models/new-calender-models/day-item';
import { MonthDays } from '../models/new-calender-models/month-days';
import { WeekDayName } from '../models/new-calender-models/week-day-name';
import { SelectDay } from '../models/new-calender-models/select-day';
import {
  digitsEnToFa,
  digitsFaToEn,
  digitsArToFa,
} from '@persian-tools/persian-tools';
import { timeAgo, remainingTime } from '@persian-tools/persian-tools';

@Injectable({
  providedIn: 'root',
})
export class NewCalendarService {
  constructor() { }

  public getToDayMonthJalali(): MonthDays {
    const today = new Date();
    const jalaliDate = toJalaali(today);
    let result = this.GetJalaliMonthDays(jalaliDate.jm, jalaliDate.jy);
    result.specificDay = jalaliDate.jd;
    return result;
  }

  public getToDayMonthGregorian(): MonthDays {
    const today = new Date();
    const gregorianMonth = today.getMonth() + 1;
    const gregorianYear = today.getFullYear();
    let result = this.getGregorianMonthDays(gregorianMonth, gregorianYear);
    result.specificDay = today.getDate();
    return result;
  }

  public getYear(pageNumber: number, isJalali: boolean): YearItem[] {
    if (isJalali) {
      return this.getJalaliYear(pageNumber);
    } else {
      return this.getGregorianYear(pageNumber);
    }
  }

  private getJalaliYear(pageNumber: number): YearItem[] {
    const currentYear = toJalaali(new Date()).jy;
    const startYear = currentYear - 10 + pageNumber * 10;
    const years: YearItem[] = [];
    for (let i = 0; i < 20; i++) {
      years.push(new YearItem(startYear + i));
    }
    return years;
  }

  private getGregorianYear(pageNumber: number): YearItem[] {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 10 + pageNumber * 10;
    const years: YearItem[] = [];
    for (let i = 0; i < 20; i++) {
      years.push(new YearItem(startYear + i));
    }
    return years;
  }

  public getMonth(isJalali: boolean): MonthItem[] {
    if (isJalali) {
      return this.getJalaliMonth();
    } else {
      return this.getGregorianMonth();
    }
  }

  private getJalaliMonth(): MonthItem[] {
    const months = [
      { name: 'فروردین', summary: 'فر', cssClass: 'month-farvardin' },
      { name: 'اردیبهشت', summary: 'ار', cssClass: 'month-ordibehesht' },
      { name: 'خرداد', summary: 'خر', cssClass: 'month-khordad' },
      { name: 'تیر', summary: 'تی', cssClass: 'month-tir' },
      { name: 'مرداد', summary: 'مر', cssClass: 'month-mordad' },
      { name: 'شهریور', summary: 'شه', cssClass: 'month-shahrivar' },
      { name: 'مهر', summary: 'مه', cssClass: 'month-mehr' },
      { name: 'آبان', summary: 'آب', cssClass: 'month-aban' },
      { name: 'آذر', summary: 'آذ', cssClass: 'month-azar' },
      { name: 'دی', summary: 'دی', cssClass: 'month-dey' },
      { name: 'بهمن', summary: 'به', cssClass: 'month-bahman' },
      { name: 'اسفند', summary: 'اس', cssClass: 'month-esfand' },
    ];
    return months.map(
      (month, index) =>
        new MonthItem(month.name, index + 1, month.cssClass, month.summary)
    );
  }

  private getGregorianMonth(): MonthItem[] {
    const months = moment.months();
    const monthShortNames = moment.monthsShort();
    return months.map(
      (month, index) =>
        new MonthItem(
          month,
          index + 1,
          `month-${monthShortNames[index].toLowerCase()}`,
          monthShortNames[index]
        )
    );
  }

  public getMonthDays(
    monthNumber: number,
    yearNumber: number,
    isJalali: boolean
  ): MonthDays {
    if (isJalali) {
      return this.GetJalaliMonthDays(monthNumber, yearNumber);
    } else {
      return this.getGregorianMonthDays(monthNumber, yearNumber);
    }
  }

  private GetJalaliMonthDays(
    monthNumber: number,
    yearNumber: number
  ): MonthDays {
    const daysInMonth = jalaaliMonthLength(yearNumber, monthNumber);
    const firstDayOfMonth = toGregorian(yearNumber, monthNumber, 1);
    const firstDate = new Date(
      firstDayOfMonth.gy,
      firstDayOfMonth.gm - 1,
      firstDayOfMonth.gd
    );
    const firstDayOfWeek = moment(firstDate).day();

    const days: DayItem[] = [];
    // Fill the empty days before the first day of the month
    for (let i = 0; i < (firstDayOfWeek === 6 ? 0 : firstDayOfWeek + 1); i++) {
      days.push(new DayItem('', -1, '', 0, undefined));
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const gDate = toGregorian(yearNumber, monthNumber, day);
      const date = new Date(gDate.gy, gDate.gm - 1, gDate.gd);
      let dateString = `${gDate.gy.toString().padStart(4, '0')}-${gDate.gm
        .toString()
        .padStart(2, '0')}-${gDate.gd
          .toString()
          .padStart(2, '0')}T00:00:00.000Z`;
      const dayOfWeekName = moment(date).format('dddd');
      days.push(
        new DayItem(
          dayOfWeekName,
          gDate.gd,
          dayOfWeekName.slice(0, 2),
          day,
          dateString
        )
      );
    }

    // Fill the empty days to make the total number of items 42
    while (days.length < 42) {
      days.push(new DayItem('', -1, '', 0, undefined));
    }

    return new MonthDays(
      this.getJalaliMonthName(monthNumber),
      monthNumber,
      this.getJalaliMonthColor(monthNumber),
      this.getJalaliMonthSummaryName(monthNumber),
      yearNumber,
      days
    );
  }

  private getJalaliMonthName(monthNumber: number): string {
    const months = [
      'فروردین',
      'اردیبهشت',
      'خرداد',
      'تیر',
      'مرداد',
      'شهریور',
      'مهر',
      'آبان',
      'آذر',
      'دی',
      'بهمن',
      'اسفند',
    ];
    return months[monthNumber - 1];
  }

  private getJalaliMonthSummaryName(monthNumber: number): string {
    return this.getJalaliMonthName(monthNumber).slice(0, 3);
  }

  private getGregorianMonthDays(
    monthNumber: number,
    yearNumber: number
  ): MonthDays {
    const daysInMonth = moment({
      year: yearNumber,
      month: monthNumber - 1,
    }).daysInMonth();
    const firstDate = new Date(yearNumber, monthNumber - 1, 1);
    const firstDayOfWeek = moment(firstDate).day();

    const days: DayItem[] = [];
    // Fill the empty days before the first day of the month
    for (let i = 0; i < (firstDayOfWeek === 6 ? 0 : firstDayOfWeek + 1); i++) {
      days.push(new DayItem('', -1, '', 0, undefined));
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(yearNumber, monthNumber - 1, day);
      const dateString = date.toISOString();
      const dayOfWeekName = moment(date).format('dddd');
      days.push(
        new DayItem(
          dayOfWeekName,
          date.getDay(),
          dayOfWeekName.slice(0, 2),
          day,
          dateString
        )
      );
    }

    // Fill the empty days to make the total number of items 42
    while (days.length < 42) {
      days.push(new DayItem('', -1, '', 0, undefined));
    }

    return new MonthDays(
      moment.months()[monthNumber - 1],
      monthNumber,
      this.getGregorianMonthColor(monthNumber),
      moment.monthsShort()[monthNumber - 1],
      yearNumber,
      days
    );
  }

  private getGregorianMonthColor(monthNumber: number): string {
    const classes = [
      'month-jan',
      'month-feb',
      'month-mar',
      'month-apr',
      'month-may',
      'month-jun',
      'month-jul',
      'month-aug',
      'month-sep',
      'month-oct',
      'month-nov',
      'month-dec',
    ];
    return classes[monthNumber - 1];
  }

  private getJalaliMonthColor(monthNumber: number): string {
    const classes = [
      'month-farvardin',
      'month-ordibehesht',
      'month-khordad',
      'month-tir',
      'month-mordad',
      'month-shahrivar',
      'month-mehr',
      'month-aban',
      'month-azar',
      'month-dey',
      'month-bahman',
      'month-esfand',
    ];
    return classes[monthNumber - 1];
  }

  public ConvertToJalali(month: number, year: number): MonthDays {
    const JalaliDate = toJalaali(new Date(year, month - 1, 1));
    return this.GetJalaliMonthDays(JalaliDate.jm, JalaliDate.jy);
  }

  public ConvertToGregorian(month: number, year: number): MonthDays {
    const firstJalaliDate = toGregorian(year, month, 1);
    return this.getGregorianMonthDays(firstJalaliDate.gm, firstJalaliDate.gy);
  }

  public TaminToGregorian(inputDate: string) {
    const regexBarcode: RegExp = /(?<year>\d{4})(?<month>\d{2})(?<day>\d{1,2})/;
    const testDatePattern: boolean = regexBarcode.test(inputDate);
    if (!testDatePattern) {
      return;
    }
    const groups = inputDate.match(regexBarcode)!.groups;

    const year: number = Number(groups?.['year']);
    const month: number = Number(groups?.['month']);
    const day: number = Number(groups?.['day']);
    var gregorianDate = toGregorian(year, month, day);
    return `${gregorianDate.gy}-${gregorianDate.gm
      .toString()
      .padStart(2, '0')}-${gregorianDate.gd
        .toString()
        .padStart(2, '0')}T00:00:00`;
  }

  public ToGregorian(inputDate: string) {
    const regexBarcode: RegExp =
      /(?<year>\d{4})(-|\/)(?<month>\d{1,2})(-|\/)(?<day>\d{1,2})/;
    const testDatePattern: boolean = regexBarcode.test(inputDate);
    if (!testDatePattern) {
      return;
    }
    const groups = inputDate.match(regexBarcode)!.groups;

    const year: number = Number(groups?.['year']);
    const month: number = Number(groups?.['month']);
    const day: number = Number(groups?.['day']);
    if (year >= 1300 && year <= 1500) {
      var gregorianDate = toGregorian(year, month, day);

      return `${gregorianDate.gy}-${gregorianDate.gm
        .toString()
        .padStart(2, '0')}-${gregorianDate.gd.toString().padStart(2, '0')}`;
    }

    return inputDate;
  }

  public ToJalali(inputDate: string | undefined) {
    if (inputDate == undefined) {
      return inputDate;
    }
    const regexBarcode: RegExp =
      /(?<year>\d{4})(-|\/)(?<month>\d{1,2})(-|\/)(?<day>\d{1,2})/;
    const testDatePattern: boolean = regexBarcode.test(inputDate);
    if (!testDatePattern) {
      return;
    }
    const groups = inputDate.match(regexBarcode)!.groups;

    const year: number = Number(groups?.['year']);
    const month: number = Number(groups?.['month']);
    const day: number = Number(groups?.['day']);
    if (year >= 1300 && year <= 1500) {
      return inputDate;
    }
    var jalaliDate = toJalaali(year, month, day);
    return `${jalaliDate.jy}-${jalaliDate.jm
      .toString()
      .padStart(2, '0')}-${jalaliDate.jd.toString().padStart(2, '0')}`;
  }

  public GetNow() {
    const now = new Date();

    return `${now.getFullYear()}-${now
      .getMonth()
      .toString()
      .padStart(2, '0')}-${now
        .getDate()
        .toString()
        .padStart(
          2,
          '0'
        )}T${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}`;
  }

  public GetNowDate() {
    const now = new Date();
    return `${now.getFullYear()}-${now
      .getMonth()
      .toString()
      .padStart(2, '0')}-${(now
        .getDate()-1)
        .toString()
        .padStart(
          2,
          '0'
        )}T00:00:00.000Z`;
  }

  public ToGregorianDateTime(inputDate: string) {
    const regexBarcode: RegExp =
      /(?<year>\d{4})(-|\/)(?<month>\d{1,2})(-|\/)(?<day>\d{1,2})/;
    const testDatePattern: boolean = regexBarcode.test(inputDate);
    if (!testDatePattern) {
      return;
    }
    const groups = inputDate.match(regexBarcode)!.groups;

    const year: number = Number(groups?.['year']);
    const month: number = Number(groups?.['month']);
    const day: number = Number(groups?.['day']);
    if (year >= 1300 && year <= 1500) {
      var gregorianDate = toGregorian(year, month, day);

      return `${gregorianDate.gy}-${gregorianDate.gm
        .toString()
        .padStart(2, '0')}-${gregorianDate.gd
          .toString()
          .padStart(2, '0')}T00:00:00`;
    }

    return `${year}-${month.toString().padStart(2, '0')}-${day
      .toString()
      .padStart(2, '0')}T00:00:00`;
  }

  public getWeekDayName(isJalali: boolean): WeekDayName[] {
    if (isJalali) {
      return this.getJalaliWeekDayName();
    } else {
      return this.getGregorianWeekDayName();
    }
  }

  private getJalaliWeekDayName(): WeekDayName[] {
    const daysOfWeek = [
      'شنبه',
      'یک‌شنبه',
      'دوشنبه',
      'سه‌شنبه',
      'چهارشنبه',
      'پنج‌شنبه',
      'جمعه',
    ];
    return daysOfWeek.map((dayName, index) => {
      const shortName = dayName.slice(0, 2);
      return new WeekDayName(index, shortName, dayName);
    });
  }

  private getGregorianWeekDayName(): WeekDayName[] {
    const daysOfWeek = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    return daysOfWeek.map((dayName, index) => {
      const shortName = dayName.slice(0, 2);
      return new WeekDayName(index, shortName, dayName);
    });
  }

  public TypeDate(elementId: string): string {
    let expiteValue = (<HTMLInputElement>(
      document.getElementById(`${elementId}`)
    )).value;

    expiteValue = digitsFaToEn(expiteValue);
    const yearDatePattern: RegExp = /^(?<year>\d{4})$/;

    const testyearDatePattern: boolean = yearDatePattern.test(expiteValue);
    if (testyearDatePattern) {
      return `${expiteValue}/`;
    }

    const monthDatePattern: RegExp = /^(?<year>\d{4})(\/{1})(?<month>\d{2})$/;

    const testmonthDatePattern: boolean = monthDatePattern.test(expiteValue);
    if (testmonthDatePattern) {
      const groups = expiteValue.match(monthDatePattern)?.groups;
      const year: number = Number(groups?.['year']);
      const month: number = Number(groups?.['month']);

      let monthString = month.toString();
      if (month < 10) {
        monthString = monthString.padStart(2, '0');
      } else if (month > 12) {
        monthString = '12';
      }

      return `${year}/${monthString}/`;
    }

    const fullDatePattern: RegExp =
      /^(?<year>\d{4})(\/{1})(?<month>\d{1,2})(\/{1})(?<day>\d{1,})/;
    const testfullDatePattern: boolean = fullDatePattern.test(expiteValue);
    if (testfullDatePattern) {
      const groups = expiteValue.match(fullDatePattern)?.groups;
      const year: number = Number(groups?.['year']);
      const month: number = Number(groups?.['month']);
      const day: number = Number(groups?.['day']);

      let monthString = month.toString();
      if (month < 10) {
        monthString = monthString.padStart(2, '0');
      } else if (month > 12) {
        monthString = '12';
      }

      let dayString = day.toString();
      if (day < 10) {
        dayString = dayString.padStart(2, '0');
      } else if (day > 31) {
        dayString = '30';
      }

      let result = `${year}-${monthString}-${dayString}T12:00:00`;
      if (dayString == '00') {
        return `${year}/${monthString}/0`;
      }
      return this.ToGregorianDateTime(result) ?? result;
    }

    return expiteValue;
  }

  public dayAgo(date: string) {
    var date = date.replaceAll('-', '/');
    var date = date.replaceAll('T', ' ');

    const dateParts = date.split(' ')[0].split('/');
    const gYear = parseInt(dateParts[0], 10);
    const gMonth = parseInt(dateParts[1], 10);
    const gDay = parseInt(dateParts[2], 10);

    const jDate = toJalaali(gYear, gMonth, gDay);
    const jalaliDate = `${jDate.jy}/${jDate.jm
      .toString()
      .padStart(2, '0')}/${jDate.jd.toString().padStart(2, '0')} 12:00:00`;

    console.log('jalaliDate', jalaliDate);

    return timeAgo(jalaliDate);
  }
}
